import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  TablePagination,
  TextField,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  Stack,
  Alert,
  Tabs,
  Tab,
  Avatar,
  Chip,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider
} from '@mui/material';
import ExportPatients from './ExportPatients';
import api from '../api/axios';
import { useNavigate, useLocation } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EventIcon from '@mui/icons-material/Event';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { AppointmentCalendar } from './Calendar';
import CreatePatientForm from './CreatePatientForm';
import { useThemeContext } from '../context/ThemeContext';

interface Patient {
  id: string;
  cliniko_id?: string;
  first_name: string;
  last_name: string;
  email: string;
  appointments?: Array<{
    id: string;
    starts_at: string;
    appointment_type?: {
      name: string;
    };
    status?: string;
  }>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

type ViewMode = 'cards' | 'list' | 'table';

const PatientManager: React.FC = () => {
  const { mode } = useThemeContext();
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [localPatients, setLocalPatients] = useState<Patient[]>([]);
  const [filteredLocalPatients, setFilteredLocalPatients] = useState<Patient[]>([]);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list'); // Default to compact list view
  const navigate = useNavigate();
  const location = useLocation();

  // Determine gradient colors based on theme mode
  const headerGradient = mode === 'dark' 
    ? 'linear-gradient(90deg, #0d1543 0%, #00a09d 100%)'
    : 'linear-gradient(90deg, #1a2980 0%, #26d0ce 100%)';
  
  const cardHeaderGradient = mode === 'dark'
    ? 'linear-gradient(90deg, rgba(38, 208, 206, 0.1) 0%, rgba(26, 41, 128, 0.05) 100%)'
    : 'linear-gradient(90deg, rgba(26, 41, 128, 0.05) 0%, rgba(38, 208, 206, 0.1) 100%)';

  // Fetch local patients
  const fetchLocalPatients = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/patients');
      const patients = response.data.patients || [];
      
      // Initialize appointments as empty arrays
      const patientsWithEmptyAppointments = patients.map((patient: Patient) => ({
        ...patient,
        appointments: []
      }));
      
      setLocalPatients(patientsWithEmptyAppointments);
      setFilteredLocalPatients(patientsWithEmptyAppointments);
      
      // After getting patients, fetch appointments for those with Cliniko IDs
      fetchAppointmentsForPatients(patientsWithEmptyAppointments);
    } catch (err) {
      console.error('Error fetching local patients:', err);
      setError('Failed to fetch local patients');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch upcoming appointments for patients with Cliniko IDs
  const fetchAppointmentsForPatients = async (patients: Patient[]) => {
    try {
      setIsLoadingAppointments(true);
      
      // Get patients with Cliniko IDs
      const patientsWithClinikoIds = patients.filter(p => p.cliniko_id);
      
      if (patientsWithClinikoIds.length === 0) {
        console.log('No patients with Cliniko IDs to fetch appointments for');
        return;
      }
      
      // Get today's date in ISO format
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayIso = today.toISOString();
      
      // Set end date 3 months from now
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 3);
      const endDateIso = endDate.toISOString();
      
      // Create a map to store each patient's appointments
      const patientAppointments: Record<string, any[]> = {};
      
      // Fetch appointments for each patient in parallel
      await Promise.all(
        patientsWithClinikoIds.map(async (patient) => {
          try {
            // Skip if no Cliniko ID
            if (!patient.cliniko_id) return;
            
            const response = await api.get('/api/appointments', {
              params: {
                patient_id: patient.cliniko_id,
                starts_at_gteq: todayIso,
                ends_at_lteq: endDateIso,
                sort: 'starts_at:asc'
              }
            });
            
            // Store the appointments
            patientAppointments[patient.id] = response.data.appointments || [];
          } catch (err) {
            console.error(`Error fetching appointments for patient ${patient.id}:`, err);
          }
        })
      );
      
      // Update the patients with their appointments
      const updatedPatients = patients.map(patient => ({
        ...patient,
        appointments: patientAppointments[patient.id] || []
      }));
      
      setLocalPatients(updatedPatients);
      setFilteredLocalPatients(
        localSearchQuery ? 
          updatedPatients.filter(p => 
            p.first_name.toLowerCase().includes(localSearchQuery.toLowerCase()) ||
            p.last_name.toLowerCase().includes(localSearchQuery.toLowerCase()) ||
            p.email.toLowerCase().includes(localSearchQuery.toLowerCase()) ||
            `${p.first_name.toLowerCase()} ${p.last_name.toLowerCase()}`.includes(localSearchQuery.toLowerCase())
          ) : 
          updatedPatients
      );
    } catch (err) {
      console.error('Error fetching appointments for patients:', err);
    } finally {
      setIsLoadingAppointments(false);
    }
  };

  // Check URL for tab parameter when component mounts
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam !== null) {
      const tabIndex = parseInt(tabParam);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 3) {
        setTabValue(tabIndex);
      }
    }
    fetchLocalPatients();
  }, [location.search]);
  
  // Filter local patients based on search query
  useEffect(() => {
    if (!localSearchQuery.trim()) {
      setFilteredLocalPatients(localPatients);
      return;
    }
    
    const query = localSearchQuery.toLowerCase();
    const filtered = localPatients.filter(patient => 
      patient.first_name.toLowerCase().includes(query) || 
      patient.last_name.toLowerCase().includes(query) || 
      patient.email.toLowerCase().includes(query) ||
      `${patient.first_name.toLowerCase()} ${patient.last_name.toLowerCase()}`.includes(query)
    );
    
    setFilteredLocalPatients(filtered);
  }, [localSearchQuery, localPatients]);

  // Search Cliniko patients
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearchLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/api/search-cliniko-patients', {
        params: { query: searchQuery }
      });
      setSearchResults(response.data.patients || []);
    } catch (err) {
      console.error('Error searching patients:', err);
      setError('Failed to search patients');
    } finally {
      setSearchLoading(false);
    }
  };

  // Add patient to local database
  const handleAddPatient = async (patient: Patient) => {
    setIsLoading(true);
    try {
      await api.post('/api/patients', {
        cliniko_id: patient.id,
        first_name: patient.first_name,
        last_name: patient.last_name,
        email: patient.email
      });
      
      // Refresh local patients list
      await fetchLocalPatients();
      
      // Clear search results after successful add
      setSearchResults([]);
      setSearchQuery('');
      setTabValue(1); // Switch to local patients tab
    } catch (err) {
      console.error('Error adding patient:', err);
      setError('Failed to add patient');
    } finally {
      setIsLoading(false);
    }
  };

  // Remove patient from local database
  const handleRemovePatient = async (id: string) => {
    setIsLoading(true);
    try {
      await api.delete(`/api/patients/${id}`);
      await fetchLocalPatients();
    } catch (err) {
      console.error('Error removing patient:', err);
      setError('Failed to remove patient');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePatientClick = (patientId: string) => {
    navigate(`/client/${patientId}`);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        mb: 4
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontSize: { xs: '1.5rem', sm: '2rem' },
            fontWeight: 700,
            background: headerGradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '0.5px'
          }}
        >
          Patient Manager
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <ExportPatients />
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setTabValue(3)} // Switch to Add Client tab
            sx={{ 
              my: 1,
              borderRadius: '20px',
              background: headerGradient,
              boxShadow: '0 4px 10px rgba(38, 208, 206, 0.3)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              '&:hover': {
                background: mode === 'dark'
                  ? 'linear-gradient(90deg, #0a1030 0%, #007a78 100%)'
                  : 'linear-gradient(90deg, #15216b 0%, #1eafad 100%)',
                transform: 'translateY(-2px)',
                transition: 'transform 0.2s ease',
                boxShadow: '0 6px 15px rgba(38, 208, 206, 0.4)',
              }
            }}
          >
            Add New Client
          </Button>
        </Box>
      </Box>
      
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          mb: 4,
          bgcolor: mode === 'dark' ? 'background.paper' : 'background.paper'
        }}
      >
        <Tabs 
          value={tabValue} 
          onChange={(_: React.SyntheticEvent, newValue: number) => {
            setTabValue(newValue);
            setShowCreateForm(false); // Hide create form when switching tabs
          }} 
          sx={{ 
            background: cardHeaderGradient,
            '& .MuiTab-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              minWidth: 'auto',
              p: { xs: 1, sm: 2 },
              fontWeight: 500,
              textTransform: 'none',
              color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#555',
              '&.Mui-selected': {
                color: mode === 'dark' ? '#26d0ce' : '#1a2980',
                fontWeight: 600
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: mode === 'dark' ? '#26d0ce' : '#26d0ce',
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab 
            icon={<PersonIcon fontSize="small" />} 
            iconPosition="start" 
            label="Local Patients" 
          />
          <Tab 
            icon={<SearchIcon fontSize="small" />} 
            iconPosition="start" 
            label="Search Database" 
          />
          <Tab 
            icon={<CalendarMonthIcon fontSize="small" />} 
            iconPosition="start" 
            label="Calendar" 
          />
          <Tab 
            icon={<AddIcon fontSize="small" />} 
            iconPosition="start" 
            label="Add Client" 
          />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Search Local Patients"
            value={localSearchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />,
              endAdornment: localSearchQuery ? (
                <IconButton 
                  size="small" 
                  onClick={() => setLocalSearchQuery('')}
                  sx={{ p: 0.5 }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : undefined,
                },
                '&:hover fieldset': {
                  borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : undefined,
                },
                '&.Mui-focused fieldset': {
                  borderColor: mode === 'dark' ? '#26d0ce' : undefined,
                },
              },
            }}
          />
        </Box>
        
        {/* View Mode Toggle */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3 
        }}>
          <Typography variant="h6" sx={{ color: 'text.primary' }}>
            {filteredLocalPatients.length} Patient{filteredLocalPatients.length !== 1 ? 's' : ''}
          </Typography>
          
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)'}`,
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                '&.Mui-selected': {
                  backgroundColor: mode === 'dark' ? 'rgba(38, 208, 206, 0.15)' : 'rgba(26, 41, 128, 0.08)',
                  color: mode === 'dark' ? '#26d0ce' : '#1a2980',
                  '&:hover': {
                    backgroundColor: mode === 'dark' ? 'rgba(38, 208, 206, 0.25)' : 'rgba(26, 41, 128, 0.12)',
                  }
                },
                '&:hover': {
                  backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                }
              }
            }}
          >
            <ToggleButton value="cards" aria-label="card view">
              <Tooltip title="Card View">
                <ViewModuleIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <Tooltip title="List View">
                <ViewListIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view">
              <Tooltip title="Table View">
                <TableRowsIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress sx={{ color: mode === 'dark' ? '#26d0ce' : '#26d0ce' }} />
          </Box>
        ) : localPatients.length > 0 ? (
          <>
            {filteredLocalPatients.length === 0 && localSearchQuery && (
              <Box sx={{ mb: 3 }}>
                <Alert severity="info">
                  No patients match your search criteria: "{localSearchQuery}"
                </Alert>
              </Box>
            )}
            
            {/* Render based on view mode */}
            {viewMode === 'cards' && (
              <Grid container spacing={2}>
                {filteredLocalPatients.map((patient) => (
                <Grid item xs={12} sm={6} md={4} key={patient.id}>
                  <Card 
                    elevation={0}
                    sx={{
                      borderRadius: '12px',
                      border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                      transition: 'all 0.3s ease',
                      height: '100%',
                      bgcolor: mode === 'dark' ? 'background.paper' : 'background.paper',
                      '&:hover': {
                        boxShadow: mode === 'dark' 
                          ? '0 8px 20px rgba(0, 0, 0, 0.5)' 
                          : '0 8px 20px rgba(0, 0, 0, 0.12)',
                        transform: 'translateY(-4px)',
                        borderColor: 'transparent',
                        cursor: 'pointer'
                      },
                      p: 0,
                      overflow: 'hidden'
                    }}
                    onClick={() => handlePatientClick(patient.id)}
                  >
                    <Box sx={{ 
                      p: 1, 
                      background: cardHeaderGradient,
                      borderBottom: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar 
                            sx={{ 
                              width: 36, 
                              height: 36, 
                              bgcolor: 'primary.main',
                              background: headerGradient,
                              fontSize: '1rem'
                            }}
                          >
                            {patient.first_name.charAt(0)}{patient.last_name.charAt(0)}
                          </Avatar>
                          <Typography 
                            variant="subtitle1" 
                            component="div"
                            sx={{ fontWeight: 600 }}
                          >
                            {`${patient.first_name} ${patient.last_name}`}
                          </Typography>
                        </Box>
                        
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemovePatient(patient.id);
                          }}
                          disabled={isLoading}
                          sx={{ 
                            ml: 'auto', 
                            p: 0.5,
                            color: mode === 'dark' ? '#f44336' : undefined,
                            '&:hover': {
                              backgroundColor: 'rgba(244, 67, 54, 0.1)'
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <EmailIcon fontSize="small" sx={{ color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)' }} />
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%'
                          }}
                        >
                          {patient.email}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip
                          size="small"
                          label="Hydralyfe ID"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.7rem',
                            borderRadius: '4px',
                            backgroundColor: mode === 'dark' ? 'rgba(38, 208, 206, 0.15)' : 'rgba(38, 208, 206, 0.1)',
                            borderColor: mode === 'dark' ? 'rgba(38, 208, 206, 0.5)' : 'rgba(38, 208, 206, 0.3)',
                            color: mode === 'dark' ? '#26d0ce' : '#1a2980'
                          }}
                        />
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%'
                          }}
                        >
                          {patient.cliniko_id || 'Not linked'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            )}
            
            {viewMode === 'list' && (
              <Grid container spacing={2}>
                {filteredLocalPatients.map((patient) => (
                  <Grid item xs={12} sm={6} md={4} key={patient.id}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        borderRadius: '8px',
                        border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                        bgcolor: mode === 'dark' ? 'background.paper' : 'background.paper',
                        overflow: 'hidden',
                        height: '100%',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: mode === 'dark' 
                            ? '0 4px 12px rgba(0, 0, 0, 0.3)' 
                            : '0 4px 12px rgba(0, 0, 0, 0.1)',
                          transform: 'translateY(-2px)',
                          borderColor: mode === 'dark' ? 'rgba(38, 208, 206, 0.3)' : 'rgba(26, 41, 128, 0.2)',
                        }
                      }}
                      onClick={() => handlePatientClick(patient.id)}
                    >
                      <Box sx={{ p: 2 }}>
                        {/* Header with avatar and delete button */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 1.5
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar 
                              sx={{ 
                                width: 28, 
                                height: 28, 
                                background: headerGradient,
                                fontSize: '0.75rem'
                              }}
                            >
                              {patient.first_name.charAt(0)}{patient.last_name.charAt(0)}
                            </Avatar>
                            <Typography 
                              variant="subtitle2" 
                              sx={{ 
                                fontWeight: 600,
                                color: 'text.primary',
                                fontSize: '0.875rem'
                              }}
                            >
                              {`${patient.first_name} ${patient.last_name}`}
                            </Typography>
                          </Box>
                          
                          <IconButton 
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemovePatient(patient.id);
                            }}
                            disabled={isLoading}
                            sx={{
                              p: 0.25,
                              color: mode === 'dark' ? '#f44336' : undefined,
                              '&:hover': {
                                backgroundColor: 'rgba(244, 67, 54, 0.1)'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        
                        {/* Email */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5, 
                          mb: 1,
                          minHeight: '20px'
                        }}>
                          <EmailIcon 
                            fontSize="small" 
                            sx={{ 
                              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                              flexShrink: 0
                            }} 
                          />
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontSize: '0.75rem',
                              lineHeight: 1.2
                            }}
                          >
                            {patient.email}
                          </Typography>
                        </Box>
                        
                        {/* Hydralyfe ID */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5,
                          minHeight: '24px'
                        }}>
                          <Chip
                            size="small"
                            label="ID"
                            variant="outlined"
                            sx={{ 
                              fontSize: '0.5rem',
                              height: '16px',
                              minWidth: '24px',
                              borderRadius: '8px',
                              backgroundColor: mode === 'dark' ? 'rgba(38, 208, 206, 0.1)' : 'rgba(38, 208, 206, 0.05)',
                              borderColor: mode === 'dark' ? 'rgba(38, 208, 206, 0.3)' : 'rgba(38, 208, 206, 0.2)',
                              color: mode === 'dark' ? '#26d0ce' : '#1a2980',
                              '& .MuiChip-label': {
                                px: 0.5
                              }
                            }}
                          />
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontSize: '0.75rem',
                              lineHeight: 1.2
                            }}
                          >
                            {patient.cliniko_id || 'Not linked'}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
            
            {viewMode === 'table' && (
              <TableContainer 
                component={Paper} 
                sx={{ 
                  borderRadius: '12px',
                  border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                  bgcolor: mode === 'dark' ? 'background.paper' : 'background.paper',
                  boxShadow: 'none'
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ 
                      background: cardHeaderGradient,
                      '& .MuiTableCell-root': {
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        color: 'text.primary',
                        border: 'none'
                      }
                    }}>
                      <TableCell>Patient</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Hydralyfe ID</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredLocalPatients.map((patient) => (
                      <TableRow 
                        key={patient.id}
                        hover
                        onClick={() => handlePatientClick(patient.id)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: mode === 'dark' 
                              ? 'rgba(38, 208, 206, 0.05)' 
                              : 'rgba(26, 41, 128, 0.02)',
                          },
                          '& .MuiTableCell-root': {
                            borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar 
                              sx={{ 
                                width: 24, 
                                height: 24, 
                                background: headerGradient,
                                fontSize: '0.75rem'
                              }}
                            >
                              {patient.first_name.charAt(0)}{patient.last_name.charAt(0)}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {`${patient.first_name} ${patient.last_name}`}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '200px'
                            }}
                          >
                            {patient.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {patient.cliniko_id || 'Not linked'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton 
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemovePatient(patient.id);
                            }}
                            disabled={isLoading}
                            sx={{
                              color: mode === 'dark' ? '#f44336' : undefined,
                              '&:hover': {
                                backgroundColor: 'rgba(244, 67, 54, 0.1)'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              borderRadius: '16px',
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
              border: `1px dashed ${mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: 2 
            }}>
              <Avatar 
                sx={{ 
                  width: 60, 
                  height: 60, 
                  bgcolor: mode === 'dark' ? 'rgba(38, 208, 206, 0.1)' : 'rgba(26, 41, 128, 0.1)',
                  color: mode === 'dark' ? '#26d0ce' : '#1a2980'
                }}
              >
                <PersonIcon fontSize="large" />
              </Avatar>
              <Typography variant="h6" color="text.secondary">
                No patients found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mb: 2 }}>
                You haven't added any patients yet. You can add patients manually or search for them in the database.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setTabValue(3)}
                sx={{ 
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 500,
                  borderColor: mode === 'dark' ? '#26d0ce' : '#1a2980',
                  color: mode === 'dark' ? '#26d0ce' : '#1a2980',
                  '&:hover': {
                    borderColor: mode === 'dark' ? '#7ffffd' : '#26d0ce',
                    backgroundColor: mode === 'dark' ? 'rgba(38, 208, 206, 0.1)' : 'rgba(38, 208, 206, 0.05)'
                  }
                }}
              >
                Add Your First Patient
              </Button>
            </Box>
          </Paper>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ 
          mb: 3, 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          gap: 1 
        }}>
          <TextField
            fullWidth
            label="Search Patients"
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            onKeyPress={(e: React.KeyboardEvent) => e.key === 'Enter' && handleSearch()}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : undefined,
                },
                '&:hover fieldset': {
                  borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : undefined,
                },
                '&.Mui-focused fieldset': {
                  borderColor: mode === 'dark' ? '#26d0ce' : undefined,
                },
              },
            }}
          />
          <Button 
            variant="contained" 
            onClick={handleSearch}
            sx={{ 
              minWidth: { xs: '100%', sm: '100px' },
              mt: { xs: 1, sm: 0 },
              background: headerGradient,
              '&:hover': {
                background: mode === 'dark'
                  ? 'linear-gradient(90deg, #0a1030 0%, #007a78 100%)'
                  : 'linear-gradient(90deg, #15216b 0%, #1eafad 100%)',
              }
            }}
          >
            Search
          </Button>
        </Box>

        {searchLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress sx={{ color: mode === 'dark' ? '#26d0ce' : '#26d0ce' }} />
          </Box>
        ) : searchResults.length > 0 ? (
          <TableContainer 
            component={Paper} 
            sx={{ 
              maxWidth: '100%', 
              overflowX: 'auto',
              bgcolor: mode === 'dark' ? 'background.paper' : 'background.paper',
              borderRadius: '12px',
              boxShadow: mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.2)' : '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchResults.map((patient) => (
                  <TableRow key={patient.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {`${patient.first_name} ${patient.last_name}`}
                    </TableCell>
                    <TableCell sx={{ 
                      maxWidth: { xs: '120px', sm: '200px' },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {patient.email}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        onClick={() => handleAddPatient(patient)}
                        variant="contained"
                        size="small"
                        disabled={isLoading}
                        sx={{ 
                          minWidth: 'auto', 
                          px: 1,
                          background: headerGradient,
                          '&:hover': {
                            background: mode === 'dark'
                              ? 'linear-gradient(90deg, #0a1030 0%, #007a78 100%)'
                              : 'linear-gradient(90deg, #15216b 0%, #1eafad 100%)',
                          }
                        }}
                      >
                        {isLoading ? '...' : 'Add'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : searchQuery && !searchLoading && (
          <Typography>No results found</Typography>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <AppointmentCalendar />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <CreatePatientForm 
          onSuccess={(patient) => {
            // After creating patient, refresh local patients and switch to that tab
            fetchLocalPatients();
            setTabValue(1); // Switch to local patients tab
          }}
        />
      </TabPanel>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      )}
    </Box>
  );
};

export default PatientManager; 